import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Loader2, Lock, ArrowRight, LogOut, Filter, Search, Download, Mail, Phone, Building2, Newspaper, Users } from "lucide-react";
import Logo from "@/components/Logo";
import BlogAdmin from "@/components/BlogAdmin";

const TOKEN_KEY = "econation_admin_token";

const SOURCE_LABELS = {
  contact: "Contact",
  eligibility: "Audit énergétique",
  assessment: "Bilan",
  expert: "Expert",
};
const SECTOR_LABELS = {
  industry: "Industrie",
  commercial: "Tertiaire",
  residential: "Résidentiel",
  unknown: "Non précisé",
};

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
function clearToken() { localStorage.removeItem(TOKEN_KEY); }

async function apiGet(path) {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (res.status === 401) { clearToken(); throw new Error("Session expirée"); }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function LoginForm({ onOk }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Connexion impossible");
      setToken(data.token);
      onOk(data);
    } catch (err) {
      setError(err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen grid place-items-center bg-gradient-to-br from-[#0A0F1A] via-[#064E3B] to-[#0A0F1A] p-4">
      <div className="absolute inset-0 bg-grid-soft opacity-[0.06] pointer-events-none" />
      <form onSubmit={submit} className="relative w-full max-w-md bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        <div className="flex justify-center mb-8">
          <Logo variant="light" showTagline />
        </div>
        <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#10B981] font-semibold mb-3 justify-center">
          <Lock className="h-3.5 w-3.5" /> Espace administrateur
        </div>
        <h1 className="text-white font-display text-2xl font-bold text-center mb-8">Connexion</h1>

        {error && <div data-testid="admin-login-error" className="mb-4 p-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-200 text-sm">{error}</div>}

        <label className="block text-white/70 text-xs uppercase tracking-widest mb-2">E-mail</label>
        <input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]"
          placeholder="direction@econation.fr"
        />
        <label className="block text-white/70 text-xs uppercase tracking-widest mb-2 mt-5">Mot de passe</label>
        <input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]"
          placeholder="••••••••"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0055A4] hover:bg-[#003D7A] text-white font-semibold py-3.5 shadow-[0_10px_30px_-10px_rgba(249,115,22,0.6)] disabled:opacity-50 transition-all"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4" /></>}
        </button>
        <Link to="/" className="block mt-6 text-center text-white/50 hover:text-white/80 text-xs">← Retour au site</Link>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_source: {}, by_sector: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [ls, st] = await Promise.all([apiGet("/admin/leads"), apiGet("/admin/leads/stats")]);
        setLeads(ls);
        setStats(st);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = leads.filter((l) => {
    if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (l.full_name || "").toLowerCase().includes(s) ||
      (l.email || "").toLowerCase().includes(s) ||
      (l.company || "").toLowerCase().includes(s) ||
      (l.message || "").toLowerCase().includes(s)
    );
  });

  const exportCsv = () => {
    const headers = ["Date", "Source", "Nom", "Email", "Téléphone", "Entreprise", "Secteur", "Projet", "Message"];
    const rows = filtered.map((l) => [
      new Date(l.created_at).toLocaleString("fr-FR"),
      SOURCE_LABELS[l.source] || l.source,
      l.full_name, l.email, l.phone || "", l.company || "",
      SECTOR_LABELS[l.sector] || l.sector,
      l.project_type || "", (l.message || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `econation-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <div className="bg-white border-b border-black/[0.06] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo variant="dark" />
            <span className="hidden sm:inline text-[11px] uppercase tracking-[0.24em] text-[#94A3B8] border-l border-black/10 pl-4">Admin</span>
          </div>
          <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] px-3 py-2 rounded-lg hover:bg-black/[0.04]">
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "leads" ? "bg-[#0F172A] text-white" : "bg-white border border-black/[0.08] text-[#475569] hover:text-[#0F172A]"}`}
          >
            <Users className="h-4 w-4" /> Leads
          </button>
          <button
            data-testid="tab-blog"
            onClick={() => setTab("blog")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "blog" ? "bg-[#0F172A] text-white" : "bg-white border border-black/[0.08] text-[#475569] hover:text-[#0F172A]"}`}
          >
            <Newspaper className="h-4 w-4" /> Blog
          </button>
        </div>

        {tab === "blog" ? (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">Articles du blog</h1>
            <p className="text-[#475569] mb-8">Génération IA (Claude Sonnet 4.6) · Auto-publication Lun/Mer/Ven · Sources ADEME, PNCEE, Légifrance.</p>
            <BlogAdmin />
          </>
        ) : (
          <>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">Demandes reçues</h1>
        <p className="text-[#475569] mb-8">Toutes les fiches contact, audits et éligibilités remontées depuis le site.</p>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div data-testid="stat-total" className="bg-white p-6 rounded-2xl border border-black/[0.06]">
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Total</div>
            <div className="font-display text-3xl font-bold text-[#0F172A] mt-1">{stats.total}</div>
          </div>
          {["contact", "eligibility", "expert", "assessment"].map((k) => (
            <div key={k} data-testid={`stat-${k}`} className="bg-white p-6 rounded-2xl border border-black/[0.06]">
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{SOURCE_LABELS[k]}</div>
              <div className="font-display text-3xl font-bold text-[#10B981] mt-1">{stats.by_source?.[k] || 0}</div>
            </div>
          )).slice(0, 3)}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (nom, email, entreprise, message)…"
              className="w-full h-11 pl-10 pr-3 rounded-xl border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#10B981]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[#94A3B8]" />
            <select
              data-testid="admin-source-filter"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-11 rounded-xl border border-black/[0.08] bg-white text-sm px-3 focus:outline-none focus:border-[#10B981]"
            >
              <option value="all">Toutes les sources</option>
              <option value="contact">Contact</option>
              <option value="eligibility">Audit énergétique</option>
              <option value="expert">Expert</option>
              <option value="assessment">Bilan</option>
            </select>
          </div>
          <button data-testid="admin-export" onClick={exportCsv} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#0F172A] hover:bg-[#064E3B] text-white text-sm font-medium">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {err && <div className="text-red-600 mb-4">{err}</div>}
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 text-[#10B981] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-16 text-center text-[#94A3B8]">Aucune demande ne correspond aux filtres.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F8FAFC] border-b border-black/[0.06] text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">
              <div className="col-span-3">Contact</div>
              <div className="col-span-2 hidden md:block">Entreprise</div>
              <div className="col-span-2">Secteur</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-3 hidden md:block">Date</div>
            </div>
            <ul className="divide-y divide-black/[0.05]">
              {filtered.map((l) => (
                <li key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l)} className="grid grid-cols-12 gap-4 px-6 py-4 items-center cursor-pointer hover:bg-[#F8FAFC] transition-colors">
                  <div className="col-span-3 min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{l.full_name}</div>
                    <div className="text-xs text-[#64748B] truncate">{l.email}</div>
                  </div>
                  <div className="col-span-2 hidden md:block text-sm text-[#475569] truncate">{l.company || "—"}</div>
                  <div className="col-span-2 text-sm">{SECTOR_LABELS[l.sector] || l.sector}</div>
                  <div className="col-span-2"><span className="inline-block px-2 py-1 rounded-md bg-[#10B981]/10 text-[#064E3B] text-xs font-medium">{SOURCE_LABELS[l.source] || l.source}</span></div>
                  <div className="col-span-3 hidden md:block text-xs text-[#94A3B8]">{new Date(l.created_at).toLocaleString("fr-FR")}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
          </>
        )}
      </div>

      {/* Lead detail modal */}
      {selected && (
        <div data-testid="lead-detail" className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 md:p-8 bg-gradient-to-br from-[#064E3B] to-[#10B981] text-white">
              <div className="text-[11px] uppercase tracking-[0.24em] opacity-85">{SOURCE_LABELS[selected.source] || selected.source}</div>
              <h3 className="font-display text-2xl font-bold mt-1">{selected.full_name}</h3>
              <div className="text-white/80 text-sm mt-1">{new Date(selected.created_at).toLocaleString("fr-FR")}</div>
            </div>
            <div className="p-6 md:p-8 space-y-4 text-sm">
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-[#10B981]" /><a href={`mailto:${selected.email}`} className="text-[#0F172A] hover:underline">{selected.email}</a></div>
              {selected.phone && <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#10B981]" /><a href={`tel:${selected.phone}`} className="text-[#0F172A] hover:underline">{selected.phone}</a></div>}
              {selected.company && <div className="flex items-center gap-3"><Building2 className="h-4 w-4 text-[#10B981]" /><span className="text-[#0F172A]">{selected.company}</span></div>}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div><div className="text-xs uppercase text-[#94A3B8]">Secteur</div><div className="font-medium mt-1">{SECTOR_LABELS[selected.sector] || selected.sector}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">Projet</div><div className="font-medium mt-1">{selected.project_type || "—"}</div></div>
              </div>
              {selected.message && (
                <div className="pt-2">
                  <div className="text-xs uppercase text-[#94A3B8] mb-1">Message</div>
                  <div className="bg-[#F8FAFC] rounded-xl p-4 text-[#0F172A] leading-relaxed whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              <div className="pt-4 flex flex-wrap gap-3">
                <a href={`mailto:${selected.email}?subject=Votre demande ECONATION`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0055A4] text-white text-sm font-semibold">Répondre par e-mail</a>
                {selected.phone && <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#0F172A] text-[#0F172A] text-sm font-semibold hover:bg-[#0F172A] hover:text-white">Appeler</a>}
                <button onClick={() => setSelected(null)} className="ml-auto text-sm text-[#94A3B8] hover:text-[#0F172A]">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(() => setAuthed(true))
      .catch(() => { clearToken(); setAuthed(false); })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-[#0A0F1A]"><Loader2 className="h-8 w-8 text-[#10B981] animate-spin" /></div>;
  }
  if (!authed) return <LoginForm onOk={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} />;
}

// Ensure /admin is not blocked by SPA fallback if user hits it directly
export const AdminRedirect = () => <Navigate to="/admin" replace />;
